import React from "react";
import styled from "styled-components";
import COLORS from "../../../assets/Colors";
import theme from "../../../assets/theme";
import { Modal } from "@material-ui/core";
import { Formik } from "formik";
import { getErrorMessage } from "../../../helpers/functions";
import { useSnackbar } from "notistack";
import EventService from "../../../services/EventService";
import FormField from "../../common/FormField/FormField";
import {
  PrimaryOutlinedCTAButton,
  PrimaryCTAButton,
} from "../../common/Buttons";
import useLoader from "../../../hooks/useLoader";
import moment from "moment";
import { copyTemplateValidationSchema } from "../../../helpers/validationSchema";

const Template = ({ modal, setModal, setTrigger }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { setLoader } = useLoader();

  async function onFormSubmit(values) {
    try {
      setLoader({ state: true, message: "Event copying..." });
      const payload = {
        ...values,
        eventDate: moment(values?.eventDate).format("DD/MM/YYYY"),
        eventTime: moment(values?.eventTime).format("hh:mm"),
      };
      const response = await EventService.copyGivenEvent(payload);
      if (response) {
        enqueueSnackbar(response?.message, {
          variant: "success",
        });
      }
    } catch (err) {
      const message = getErrorMessage(err);
      enqueueSnackbar(message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
      setModal({ state: false });
      setTrigger(true);
    }
  }

  /**
   * @description live poll inputs
   */

  const inputs = [
    {
      name: "eventName",
      label: "Event Name",
      type: "text",
    },
    {
      name: "eventDate",
      label: "Event Date",
      type: "date",
    },
    {
      name: "eventTime",
      label: "Event Time",
      type: "time",
    },
    {
      name: "copyReason",
      label: "Reason",
      type: "select",
      menuItems: [
        { value: "dryRun", label: "Dry run" },
        { value: "live", label: "Live" },
        { value: "demo", label: "Demo" },
        { value: "other", label: "Other" },
      ],
    },
  ];

  return (
    <Modal
      open={modal?.state}
      onClose={() => setModal({ state: false })}
      disableRestoreFocus={true}
    >
      <DrawerContainer>
        <Container>
          <HeadingContainer>
            <Heading>{"Copy Event"}</Heading>
            <CloseModal
              onClick={() => setModal({ state: false })}
              src={require("../../../assets/images/closeModal.svg")}
            />
          </HeadingContainer>
          <FormContainer>
            <Formik
              initialValues={{
                copiedEventId: modal?.rowData?.eventId,
                eventDate: new Date(),
                eventTime: new Date(),
                eventName: "",
                copyReason: "",
              }}
              enableReinitialize
              validateOnBlur
              validateOnChange
              validationSchema={copyTemplateValidationSchema}
              onSubmit={onFormSubmit}
            >
              {({
                values,
                handleChange,
                errors,
                handleSubmit,
                touched,
                setFieldValue,
                setFieldError,
                handleBlur,
              }) => (
                <StyledForm onSubmit={handleSubmit}>
                  {inputs.map((input, index) => (
                    <FormField
                      key={index}
                      menuItems={input.menuItems}
                      {...{
                        input,
                        values,
                        handleChange,
                        errors,
                        handleSubmit,
                        touched,
                        setFieldValue,
                        setFieldError,
                        handleBlur,
                      }}
                    />
                  ))}

                  <ButtonContainer>
                    <PrimaryOutlinedCTAButton
                      style={{ width: "45%" }}
                      onClick={(e) => {
                        e.preventDefault();
                        setModal(false);
                      }}
                    >
                      {"Cancel"}
                    </PrimaryOutlinedCTAButton>
                    <PrimaryCTAButton
                      style={{ width: "45%" }}
                      onClick={(e) => {
                        e.preventDefault();
                        handleSubmit();
                      }}
                    >
                      {"Submit"}
                    </PrimaryCTAButton>
                  </ButtonContainer>
                </StyledForm>
              )}
            </Formik>
          </FormContainer>
        </Container>
      </DrawerContainer>
    </Modal>
  );
};

export default Template;

const Container = styled.div`
  margin: 0 15px;
  background-color: white;
  outline: none;
  border-radius: 8px;
  @media ${theme?.breakpoints?.sm_up} {
    margin: 0;
  }
`;

const DrawerContainer = styled.div`
  width: 100%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  @media ${theme?.breakpoints?.sm_up} {
    max-width: 456px;
  }
`;

const HeadingContainer = styled.div`
  padding-top: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`;

const CloseModal = styled.img`
  width: 14px;
  height: 14px;
  object-fit: contain;
  cursor: pointer;
  position: absolute;
  right: 34px;
  @media ${theme.breakpoints.sm_up} {
    right: 40px;
  }
`;

const Heading = styled.h3`
  margin: 0;
  padding: 0;
  font-size: 18px;
  font-weight: 800;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: center;
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFontExtraBold};
`;

const FormContainer = styled.p`
  padding: 0px 15px 30px 15px;
  color: ${COLORS.COLOR_DARK};
  font-size: 14px;
  font-family: ${theme.fonts.primaryFontRegular};
  line-height: 1.71;
  min-height: 200px;
  @media ${theme?.breakpoints?.sm_up} {
    padding-left: 40px;
    padding-right: 40px;
    padding-bottom: 30px;
  }
`;

const StyledForm = styled.form``;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 25px;
`;
